/** MACHETA UNIFICATĂ (v. `tipuri.ts`): macheta de situație cu fațade, exact
 *  cea de pe planul de situație, în care intră ca strat atașat STRUCTURA
 *  desenată ca în stratul «Realist» al Studioului. Comutatoarele sting
 *  clădirea (corpuri, fațade) ca să rămână structura, sau familiile ei. Un
 *  clic pe o bucată de fațadă deschide detaliile ei (`DetaliiElement`, ca pe
 *  situație); un clic pe un stâlp, o grindă, un ochi de placă deschide fișa
 *  lui (`PanouProprietati`, ca în Studio). O selecție o golește pe cealaltă. */
import { useEffect, useMemo, useState } from "react";
import type * as THREE from "three";
import type { CanonicalDoc, StoreyPlan } from "../canonical";
import type { ScenaSit } from "../plan/sit3d";
import RandareSit3D, { type VEDERI } from "../plan/RandareSit3D";
import DetaliiElement from "../fatada/DetaliiElement";
import { dimensiuniCote } from "../fatada/detalii";
import { PanouProprietati, type SelectieFem, zidarieFlat } from "../../structure/StepFem";
import { CardCorpComplex, piesaComplex } from "../../structure/Studio";
import type { FemResult } from "../../structure/tipuri";
import { FAMILII_IMPLICITE, construiesteStructura, type FamilieStructura, type StareStructura } from "../../structure/fem/strat3d";
import { asezareaStructurii } from "./cadru";
import { getArmatura3d, getSchitaFem, getStep } from "../../structure/apiStructura";
import { construiesteArmatura, type ArmaturaRezumat, type GrupArmatura } from "../../structure/armatura3d";
import { fundareaDesenata } from "../../structure/StepFem";
import * as THREE_ from "three";
import type { StareMacheta, StratAtasat, Tinta } from "./tipuri";
import { useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import "../../structure/tema.css";
import "../../structure/legacy.css";
import "./macheta.css";

export interface Props {
  projectId: string;
  scena: ScenaSit;
  /** documentul arhitecturii, adus «nord sus» — detaliile și cotele fațadei */
  docArh: CanonicalDoc | null;
  planuri?: readonly StoreyPlan[];
  /** modelul structurii (vederea complexului); `null` cât nu e */
  structura: FemResult | null;
  /** originea locală a machetei (a documentului de situație) */
  origine: [number, number];
  /** rândul de structură (cardul piesei din vederea complexului trimite la corpul ei) */
  solutie?: string | null;
  /** CORPURILE rândului (`structura.corpuri`, cu spațiul lor de pași): de acolo
   *  vin modelul și armătura fiecărui corp — complexul e geometrie, armarea
   *  stă pe corp */
  corpuri?: readonly { cod: string; spatiu: string; ansamblu?: boolean }[] | null;
  /** drumul înapoi (Studioul rândului); lipsă în harnașament */
  inapoi?: string | null;
  /** starea de pornire a comutatoarelor (harnașamentul o ia din adresă) */
  cladireInitial?: boolean;
  /** PE NIVEL + STAREA PE SĂPTĂMÂNĂ (timelapse-ul): v. `RandareSit3D` */
  perNivel?: boolean;
  stare?: StareMacheta | null;
  /** starea structurii pe corp × etaj × familie (timelapse-ul); dată, familiile
   *  se aprind toate și ea decide */
  stareStructurii?: StareStructura | null;
  /** alte straturi atașate (groapa, șantierul), construite de gazdă */
  atasateExtra?: readonly StratAtasat[];
  /** fără panoul de comutatoare (gazda are al ei) */
  faraComutatoare?: boolean;
  /** presetul camerei */
  vedere?: keyof typeof VEDERI;
}

const FAMILII: { cheie: FamilieStructura; nume: Cheie; cuNumar?: "ferestre" | "usi" | "parapete" | "nuclee" }[] = [
  { cheie: "stalpi", nume: "struct.fem.lay.stalpi" }, { cheie: "grinzi", nume: "struct.fem.lay.grinzi" },
  { cheie: "placa", nume: "struct.fem.lay.placa" }, { cheie: "peretiBeton", nume: "struct.fem.lay.pereti_beton" },
  { cheie: "zidarieConfinata", nume: "struct.fem.lay.zidarie_confinata" }, { cheie: "fundatie", nume: "struct.fem.lay.fundatie" },
  { cheie: "placaSol", nume: "struct.fem.lay.placa_sol" }, { cheie: "pereti", nume: "struct.fem.lay.pereti" },
  { cheie: "balcoane", nume: "struct.fem.lay.balcoane" },
  { cheie: "ferestre", nume: "struct.fem.lay.ferestre", cuNumar: "ferestre" }, { cheie: "usi", nume: "struct.fem.lay.usi", cuNumar: "usi" },
  { cheie: "parapete", nume: "struct.fem.lay.parapete", cuNumar: "parapete" }, { cheie: "nuclee", nume: "struct.fem.lay.nuclee", cuNumar: "nuclee" },
];

/** PIESA ALEASĂ pe vederea complexului, când nu-și știe corpul (un ochi de
 *  placă, un perete de zidărie, o fereastră): ce spune modelul despre ea —
 *  familia, id-ul de plan, corpul, nivelul, dimensiunile. Fișa întreagă
 *  (verdicte, armare) stă pe corpul ei, la pasul 2 al planului. */
export function CardPiesa({ r, sel, onClose }: { r: FemResult; sel: NonNullable<SelectieFem>; onClose: () => void }) {
  const t = useT();
  const m = r.model;
  const FAM: Record<string, Cheie> = {
    stalp: "struct.fem.lay.stalpi", grinda: "struct.fem.lay.grinzi", panou: "struct.fem.lay.placa",
    perete_beton: "struct.fem.lay.pereti_beton", zidarie_confinata: "struct.fem.lay.zidarie_confinata",
    perete_zidarie: "struct.fem.lay.pereti", fundatie: "struct.fem.lay.fundatie", radier: "struct.fem.lay.radier",
    placa_pe_sol: "struct.fem.lay.placa_sol",
  };
  type Piesa = { id_plan?: string; corp?: string; nivel?: string; b_m?: number; h_m?: number; bw_m?: number;
                 t_m?: number; L_m?: number; h_web_m?: number; id?: string; etaj?: number };
  const piesa: Piesa | null = sel.tip === "stalp" ? (m.stalpi[sel.ref] as Piesa)
    : sel.tip === "grinda" ? (m.grinzi[sel.ref] as Piesa)
    : sel.tip === "perete_beton" ? (m.pereti_beton?.[sel.ref] as Piesa)
    : sel.tip === "zidarie_confinata" ? (m.pereti_zidarie?.[sel.ref] as Piesa)
    : sel.tip === "perete_zidarie" ? ((): Piesa | null => {
        const z = zidarieFlat(m)[sel.ref] as { id?: string; nivel?: string } | undefined;
        return z ? { id_plan: z.id, nivel: z.nivel } : null;
      })()
    : sel.tip === "panou" ? ((): Piesa | null => {
        const p = m.panouri?.[sel.ref] as { nivel?: number } | undefined;
        return p ? { nivel: m.etaje.find((e) => e.nivel_diafragma === p.nivel)?.nivel } : null;
      })()
    : null;
  const dim = piesa && [
    piesa.b_m != null && piesa.h_m != null ? `${(piesa.b_m * 100).toFixed(0)} × ${(piesa.h_m * 100).toFixed(0)} cm` : null,
    piesa.bw_m != null && piesa.h_m != null ? `${(piesa.bw_m * 100).toFixed(0)} × ${(piesa.h_m * 100).toFixed(0)} cm` : null,
    piesa.t_m != null ? `t = ${(piesa.t_m * 100).toFixed(0)} cm` : null,
    piesa.L_m != null ? `L = ${piesa.L_m.toFixed(2)} m` : null,
    piesa.h_web_m != null && piesa.t_m != null ? `h = ${piesa.h_web_m.toFixed(2)} m` : null,
  ].filter(Boolean).join(" · ");
  const cheie = FAM[sel.tip];
  return (
    <div className="ob-studio-sumar" data-testid="card-piesa">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <b className="ob-disp" style={{ fontSize: 17 }}>{cheie ? t(cheie, { n: 0 }) : sel.tip}</b>
        {piesa?.id_plan && <span className="ob-chip ob-c-mut" style={{ fontSize: 10 }}>{piesa.id_plan}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={onClose} aria-label={t("comutator.inchide")}>×</button>
      </div>
      <p className="sg-muted" style={{ fontSize: 12, marginTop: 6 }}>
        {[piesa?.corp ? t("macheta.piesa.corp", { corp: piesa.corp }) : null,
          piesa?.nivel ? t("macheta.piesa.nivel", { nivel: piesa.nivel }) : null,
          dim || null].filter(Boolean).join(" · ") || t("macheta.piesa.fara_detalii")}
      </p>
    </div>
  );
}

export default function MachetaUnificata({ projectId, scena, docArh, planuri, structura, origine, solutie = null, corpuri = null, inapoi,
                                            cladireInitial = true, perNivel = false, stare = null, stareStructurii = null,
                                            atasateExtra, faraComutatoare = false, vedere = "ansamblu" }: Props) {
  const t = useT();
  const [cladire, setCladire] = useState(cladireInitial);
  const [cuStructura, setCuStructura] = useState(true);
  const [familii, setFamilii] = useState<Record<FamilieStructura, boolean>>({ ...FAMILII_IMPLICITE });
  const [selFatada, setSelFatada] = useState<string[]>([]);
  const [activ, setActiv] = useState<string | null>(null);
  const [cote, setCote] = useState(false);
  const [selStructura, setSelStructura] = useState<SelectieFem>(null);
  // straturile Studioului cerute la cerere: terenul cu săpătura, armătura
  const [cuTeren, setCuTeren] = useState(false);
  const [cuArmatura, setCuArmatura] = useState(false);
  const [armatura, setArmatura] = useState<ArmaturaRezumat | null | "lipsa">(null);
  // ARMĂTURA PE CORPURI: modelul fiecărui corp (în cadrul lui) cu rezumatul
  // armării lui, așezat în desen ca stratul structurii; `lipsa` = niciun corp
  // cu pașii de armare rulați
  type ArmCorp = { cod: string; a: GrupArmatura; etaje: { etaj: number; z0: number; z1: number }[] };
  const [armCorpuri, setArmCorpuri] = useState<ArmCorp[] | "lipsa" | null>(null);

  // STRATUL STRUCTURII: construit o dată pe model, așezat în cadrul desenului
  const strat = useMemo(() => {
    if (!structura) return null;
    const s = construiesteStructura(structura, { perNivel, mediuInMateriale: true });
    const a = asezareaStructurii(structura.model as unknown as { origine_stereo70?: number[]; theta_rad?: number; etaje?: FemResult["model"]["etaje"] }, origine);
    if (a) {
      s.grup.position.set(a.x, a.y, a.z);
      s.grup.rotation.z = a.theta;
      s.grup.updateMatrix();
    }
    return { ...s, asezat: !!a };
  }, [structura, origine, perNivel]);
  useEffect(() => () => { strat?.dispose(); }, [strat]);
  useEffect(() => {
    if (!strat) return;
    strat.grup.visible = cuStructura;
    // cu o stare pe săptămână, familiile stau toate aprinse și starea decide pe corp × etaj
    for (const [k, g] of Object.entries(strat.familii)) g.visible = stareStructurii ? true : familii[k as FamilieStructura];
    strat.aplicaStare(stareStructurii);
  }, [strat, cuStructura, familii, stareStructurii]);
  useEffect(() => { strat?.evidentiaza(selStructura); }, [strat, selStructura]);
  useEffect(() => { strat?.terenSapatura(cuTeren); }, [strat, cuTeren]);
  // ARMĂTURA vine din rezumatul serverului (`armare_3d.py`) al rândului: pe
  // vederea complexului nu există (geometrie, nu analiză), deci numai pe un
  // corp singur; se cere la prima aprindere și se ține
  const eComplex = !!structura && (!!(structura as { complex?: boolean }).complex || !structura.parametri);
  const corpuriCuPasi = useMemo(() => (corpuri ?? []).filter((c) => !c.ansamblu && c.spatiu), [corpuri]);
  useEffect(() => {
    if (!cuArmatura || armCorpuri || !projectId || projectId === "dev" || !corpuriCuPasi.length) return;
    let viu = true;
    const unCorp = async (c: { cod: string; spatiu: string }): Promise<ArmCorp | null> => {
      // modelul corpului: rezultatul Pasului 7 când a rulat (armătura e a
      // lui), altfel schița, care se construiește pe server la prima cerere
      const pas = await getStep(projectId, "model_fem", c.spatiu).catch(() => null);
      let model = (pas?.result as { model?: FemResult["model"] } | null)?.model;
      if (!model) {
        let sch = await getSchitaFem(projectId, c.spatiu).catch(() => null);
        for (let k = 0; k < 6 && sch?.se_construieste && viu; k++) {
          await new Promise((r) => setTimeout(r, 3000));
          sch = await getSchitaFem(projectId, c.spatiu).catch(() => null);
        }
        model = sch?.model as FemResult["model"] | undefined;
      }
      if (!model?.stalpi) return null;
      const rez = await getArmatura3d(projectId, c.spatiu).catch(() => null);
      if (!rez) return null;
      const r = { model } as FemResult;
      const a = construiesteArmatura(r, rez, fundareaDesenata(model));
      const az = asezareaStructurii(model as unknown as { origine_stereo70?: number[]; theta_rad?: number; etaje?: FemResult["model"]["etaje"] }, origine);
      if (az) { a.grup.position.set(az.x, az.y, az.z); a.grup.rotation.z = az.theta; a.grup.updateMatrix(); }
      const h = model.inaltime_etaj_m ?? 3;
      return { cod: c.cod, a, etaje: model.etaje.map((e, k) => ({ etaj: k, z0: e.z_m - (e.inaltime_m ?? h), z1: e.z_m })) };
    };
    Promise.all(corpuriCuPasi.map(unCorp)).then((l) => {
      if (!viu) { l.forEach((x) => x?.a.dispose()); return; }
      const bune = l.filter((x): x is ArmCorp => !!x);
      setArmCorpuri(bune.length ? bune : "lipsa");
    });
    return () => { viu = false; };
  }, [cuArmatura, armCorpuri, projectId, corpuriCuPasi, origine]);
  useEffect(() => () => { if (armCorpuri && armCorpuri !== "lipsa") armCorpuri.forEach((x) => x.a.dispose()); }, [armCorpuri]);
  const stratArmatura = useMemo<StratAtasat | null>(() => {
    if (!armCorpuri || armCorpuri === "lipsa") return null;
    const grup = new THREE_.Group(); grup.name = "armatura";
    for (const x of armCorpuri) grup.add(x.a.grup);
    return { id: "structura", grup, tinta: () => null, evidentiaza: () => undefined, dispose: () => undefined };
  }, [armCorpuri]);
  const atasate = useMemo<StratAtasat[]>(() => [...(strat ? [{
    id: "structura" as const,
    grup: strat.grup,
    tinta: (o: THREE.Object3D) => { const s = strat.tinta(o); return s ? { strat: "structura" as const, sel: s } : null; },
    evidentiaza: (x: Tinta | null) => strat.evidentiaza(x && x.strat === "structura" ? x.sel : null),
    dispose: strat.dispose,
  }] : []), ...(stratArmatura ? [stratArmatura] : []), ...(atasateExtra ?? [])], [strat, stratArmatura, atasateExtra]);
  useEffect(() => {
    if (!stratArmatura || armCorpuri === "lipsa" || !armCorpuri) return;
    stratArmatura.grup.visible = cuArmatura && cuStructura;
    // pe săptămână: barele etajelor armate deja (pe cota lor în corp)
    for (const x of armCorpuri) {
      x.a.arataDupaCota(stareStructurii ? (z) => {
        const e = x.etaje.find((et) => z >= et.z0 - 0.3 && z < et.z1 + 0.3) ?? (z < (x.etaje[0]?.z0 ?? 0) ? x.etaje[0] : undefined);
        return !!e && stareStructurii(x.cod, e.etaj, "armatura");
      } : null);
    }
  }, [stratArmatura, armCorpuri, cuArmatura, cuStructura, stareStructurii]);
  useEffect(() => {
    if (!cuArmatura || armatura || !projectId || projectId === "dev" || !solutie || eComplex || corpuriCuPasi.length) return;
    let viu = true;
    getArmatura3d(projectId, solutie).then((a) => { if (viu) setArmatura(a); }).catch(() => { if (viu) setArmatura("lipsa"); });
    return () => { viu = false; };
  }, [cuArmatura, armatura, projectId, solutie, eComplex]);
  useEffect(() => { strat?.armatura(cuArmatura && armatura && armatura !== "lipsa" ? armatura : null); }, [strat, cuArmatura, armatura]);

  // CLICUL: fațada sau structura; una o golește pe cealaltă
  const laAlegere = (eid: string | null, adauga: boolean) => {
    setSelStructura(null);
    if (!eid) { setSelFatada([]); setActiv(null); return; }
    setSelFatada((s) => (adauga ? (s.includes(eid) ? s.filter((x) => x !== eid) : [...s, eid]) : [eid]));
    setActiv(eid);
  };
  const laTinta = (x: Tinta | null) => {
    if (!x) { setSelStructura(null); return; }
    setSelFatada([]); setActiv(null);
    setSelStructura(x.sel);
  };
  const coteValori = useMemo(() => (cote && activ && docArh ? dimensiuniCote(docArh, activ) : undefined), [cote, activ, docArh]);

  return (
    <div className="ob-studio-wrap mu-wrap">
      <div className="ob-studio mu-scena">
        <RandareSit3D scena={scena} soare={{ alt: 46, az: 200 }} vedere={vedere}
                      selectie={selFatada} laAlegere={laAlegere} cote={cote ? activ : null} coteValori={coteValori}
                      planuri={planuri} cladire={cladire} atasate={atasate} laTinta={laTinta}
                      perNivel={perNivel} stare={stare} />
        {!faraComutatoare && <div className="mu-straturi">
          <div className="mu-cap">
            <span className="ob-lbl">{t("macheta.titlu")}</span>
            {inapoi && <a className="mu-inapoi" href={inapoi}>{t("macheta.inapoi")}</a>}
          </div>
          <label><input type="checkbox" checked={cladire} onChange={(e) => setCladire(e.target.checked)} />{t("macheta.strat.cladire")}</label>
          <label className={structura ? "" : "mu-lipsa"}>
            <input type="checkbox" checked={cuStructura && !!structura} disabled={!structura} onChange={(e) => setCuStructura(e.target.checked)} />
            {t("macheta.strat.structura")}{!structura && <small> · {t("macheta.strat.structura_lipsa")}</small>}
          </label>
          {structura && cuStructura && (
            <div className="mu-familii">
              {FAMILII.map((f) => (
                <label key={f.cheie}>
                  <input type="checkbox" checked={familii[f.cheie]}
                         onChange={(e) => setFamilii((v) => ({ ...v, [f.cheie]: e.target.checked }))} />
                  {f.cuNumar ? t(f.nume, { n: strat?.numar[f.cuNumar] ?? 0 }) : t(f.nume)}
                </label>
              ))}
              {/* pe complex, săpătura modelului e a primului corp și la cota lui:
                  nu se desenează până n-o scrie serverul pe tot ansamblul */}
              {structura.model.sapatura && !eComplex && (
                <label><input type="checkbox" checked={cuTeren} onChange={(e) => setCuTeren(e.target.checked)} />{t("struct.fem.lay.teren")}</label>
              )}
              {(() => {
                const lipsa = armatura === "lipsa" || armCorpuri === "lipsa";
                const nuPoate = !corpuriCuPasi.length && (eComplex || !solutie);
                return (
                  <label className={lipsa || nuPoate ? "mu-lipsa" : ""} title={nuPoate ? t("struct.studio.complex_nota") : undefined}>
                    <input type="checkbox" checked={cuArmatura && !nuPoate} disabled={lipsa || nuPoate}
                           onChange={(e) => setCuArmatura(e.target.checked)} />
                    {lipsa ? t("struct.fem.lay.armatura_nerulat") : t("struct.fem.lay.armatura")}
                    {cuArmatura && !lipsa && !nuPoate && armCorpuri == null && corpuriCuPasi.length > 0 && <small> …</small>}
                  </label>
                );
              })()}
            </div>
          )}
          <div className="mu-sfat">{t("macheta.sfat")}</div>
        </div>}
      </div>
      {structura && selStructura && (
        <aside className="ob-studio-inspector">
          {/* VEDEREA COMPLEXULUI e o geometrie, nu o analiză: piesa aleasă își
              spune corpul și id-ul de plan, iar fișa ei stă pe pagina corpului
              (ca în Studio, «Toate corpurile»); pe un singur corp, fișa întreagă */}
          {(structura as { complex?: boolean }).complex || !structura.parametri
            ? (piesaComplex(structura, selStructura)
                ? <CardCorpComplex id={projectId} solutie={solutie} r={structura} sel={selStructura} onClose={() => setSelStructura(null)} />
                : <CardPiesa r={structura} sel={selStructura} onClose={() => setSelStructura(null)} />)
            : <PanouProprietati r={structura} sel={selStructura} projectId={projectId} onClose={() => setSelStructura(null)} />}
        </aside>
      )}
      {activ && (
        <DetaliiElement projectId={projectId} doc={docArh} elemente={selFatada} activ={activ} laActiv={setActiv}
                        laInchide={() => { setSelFatada([]); setActiv(null); }} cote={cote} laCote={setCote} />
      )}
    </div>
  );
}
